export const layer3Template = `# Layer 3：Marveland 後台管理介面完整實現

> 目標：基於 Layer 1 的骨架與 Layer 2 的前台，生成後台 \`/admin\` 的完整管理介面。
> **禁止**：修改型別定義、修改 Worker API、修改前台頁面、修改路由配置。
> 輸入：Layer 1 + Layer 2 已完成的專案。
> 輸出要求：後台可登入、可管理所有內容、儲存後前台可見。

---

## 前置條件

Layer 1 與 Layer 2 已完成：
- ✅ 專案結構、型別、API 骨架
- ✅ 前台所有頁面正常運作
- ✅ CMSContext 提供 \`updateCMSData\`
- ✅ Auth 機制（Bearer token / Basic auth）

---

## 1. Admin.tsx 架構

後台為單一頁面 \`/admin\`，使用 Tab 切換不同管理功能：

\`\`\`
/admin
├── 登入頁（未登入時）
└── 管理面板（登入後）
    ├── Tab 1: 內容編輯
    ├── Tab 2: 物業管理
    ├── Tab 3: 銷售團隊
    ├── Tab 4: 最新消息
    ├── Tab 5: 查詢記錄
    ├── Tab 6: 媒體庫
    └── Tab 7: 數據分析
\`\`\`

### 登入頁
- 帳號密碼輸入
- 呼叫 \`POST /api/admin/login\`
- 成功後儲存 token 到 \`sessionStorage\`
- 顯示登入錯誤提示

### 管理面板
- 左側導航（桌面）/ 頂部 Tab（手機）
- 右上角：儲存變更按鈕、登出按鈕
- 所有編輯操作先更新本地 \`draft\` state
- 點擊「儲存變更」才 \`POST /api/cms/data\` 寫入 KV

---

## 2. Tab 1：內容編輯

編輯以下區塊的表單：
- **siteConfig**：公司名稱、標語、描述、Logo URL、社交連結
- **hero**：背景圖、標題、描述、按鈕文字與連結
- **about**：啟用開關、標題、描述、圖片
- **contact**：電話、WhatsApp、電郵、地址、服務時間
- **footer**：版權、牌照、連結清單
- **nav**：導航項目（可新增/刪除/排序）
- **services**：服務項目（可新增/刪除/編輯）
- **ownerForm**：表單標題、描述、欄位定義（動態增減）

### 表單設計原則
- 每個欄位都有 label
- 圖片 URL 欄位附帶「從媒體庫選擇」按鈕
- 開關使用 Toggle 元件
- 陣列欄位（如 nav、services）支援新增/刪除/排序
- 即時預覽（可選）

---

## 3. Tab 2：物業管理

### 物業列表
- 表格顯示：code、title、category、district、listingType、price、rentPrice、agent、featured、publishDate
- 篩選：與前台相同（地區、類型、租售類型、精選狀態、關鍵字）
- 分頁或無限滾動
- 操作：新增 / 編輯 / 複製 / 刪除

### PropertyEditor（側邊滑出 Panel）
- 從右側滑入（Framer Motion）
- 表單欄位對應 \`Property\` interface 的所有欄位
- 圖片管理：
  - 封面圖：從媒體庫選擇（single）
  - 相簿：從媒體庫選擇（multiple，可排序/刪除）
  - 平面圖：從媒體庫選擇（single）
- 負責人：下拉選擇（從 \`cmsData.agents\`）
- 標籤：輸入框，逗號分隔或 chip 形式
- 特色：textarea
- 精選：Toggle
- **儲存邏輯**：更新 \`draft.properties\`，不直接寫入 KV

### 批量導入 JSON
- textarea 輸入 JSON 陣列
- 驗證每個物件符合 \`Property\` interface
- 合併或覆蓋現有物業

### 業主放盤審批子頁籤
- 顯示 \`type: 'owner_listing'\` 的 inquiries
- 狀態：new / approved / rejected
- 操作：審批通過 / 拒絕（填寫原因）
- 審批通過後可一鍵轉為正式物業（預填 PropertyEditor）

---

## 4. Tab 3：銷售團隊

- 表格顯示：照片、姓名、職稱、WhatsApp、電郵
- 操作：新增 / 編輯 / 刪除
- 照片 URL 必須透過 MediaPicker 從媒體庫選擇
- WhatsApp 號碼儲存保留 \`852\` 前綴

---

## 5. Tab 4：最新消息

- 表格顯示：圖片、標題、日期、分類
- 操作：新增 / 編輯 / 刪除
- 圖片 URL 欄位附帶「從媒體庫選擇」按鈕
- 內容欄位：textarea（支援簡單 HTML）

---

## 6. Tab 5：查詢記錄

- 顯示所有 inquiries（\`GET /api/inquiries\`）
- 篩選：按類型、狀態、日期範圍
- 表格欄位：類型、姓名、電話、物業、狀態、日期
- 操作：更新狀態（PUT /api/inquiries/:id）
- 查看詳情彈窗

---

## 7. Tab 6：媒體庫

### 上傳
- 拖放上傳區域
- 上傳前呼叫 \`smartCompressImage\`（Layer 2 實現）
- 上傳進度顯示
- 限制提示：總容量 1GB

### 媒體列表
- 網格顯示縮圖
- 檔案名稱、大小、上傳日期
- **已用容量顯示**：計算所有媒體的 size 總和，顯示「已用 X MB / 1GB」進度條
- 操作：刪除（確認對話框）

### MediaPicker 組件
- 彈窗顯示媒體庫
- \`mode="single"\`：只能選一張，確認後回傳 URL
- \`mode="multiple"\`：可多選，確認後回傳 URL 陣列
- 搜尋功能（按檔名）

---

## 8. Tab 7：數據分析（AnalyticsDashboard）

### 資料來源
- \`GET /api/analytics?start=YYYY-MM-DD&end=YYYY-MM-DD\`
- \`GET /api/analytics/campaigns\`

### 篩選器
- 日期範圍：預設最近 7 天，按鈕：7 天 / 30 天 / 90 天 / 自訂
- Campaign 多選按鈕（從 \`/api/analytics/campaigns\` 載入）

### KPI Cards
- 總瀏覽量（pageview 總數）
- 獨立訪客（不同 sessionId 數量）
- 轉換數（conversion 總數）
- 轉換率（conversion / pageview * 100%）

### 圖表（Recharts）
- **趨勢折線圖**：每日瀏覽量 + 轉換數（雙 Y 軸）
- **來源長條圖**：按 utm_source / referrer 分組
- **熱門頁面長條圖**：按 path 分組
- **Campaign 對比表**：各 campaign 的瀏覽量、轉換、轉換率

### 使用指南區塊
- 說明總瀏覽量、獨立訪客、轉換、轉換率的定義
- 說明 Meta/Facebook 廣告必須帶 UTM 參數才能正確歸類
- 提供 UTM 參數說明

### 廣告追蹤連結產生器
- 輸入欄位：utm_source / utm_medium / utm_campaign / utm_content
- 自動產生帶 UTM 的網站首頁連結
- 一鍵複製按鈕

---

## 9. 生成完成後的驗證步驟

1. \`/admin\` 可登入（使用 Layer 1 設定的帳密）
2. 內容編輯儲存後前台可見（測試修改 hero.titleLine1）
3. 物業管理列表可使用所有篩選，每個欄位都有 label
4. 新增/編輯物業可從媒體庫選圖與平面圖
5. 銷售團隊照片可從媒體庫選擇
6. 媒體庫顯示已用容量 / 1GB
7. Analytics Dashboard 顯示正確趨勢圖
8. 廣告追蹤連結產生器可正確產生 URL
9. 登出後無法訪問管理功能
10. 未登入訪問 \`/admin\` 顯示登入頁（非空白）
11. 物業編輯的 draft state 不影響前台，直到點擊「儲存變更」
12. 批量導入 JSON 時，無效資料顯示錯誤提示而非崩潰

---

## 10. 常見陷阱（Layer 3 特別關注）

1. **Draft state 隔離**：Admin 的編輯操作必須只更新本地 draft，不直接修改 CMSContext 的 data
2. **儲存時 deep merge**：\`POST /api/cms/data\` 傳送的是整個 draft，Worker 會 deep merge
3. **型別同步**：如果新增欄位，必須同步 \`types/cms.ts\`（Layer 1 已鎖定，此處不應新增）
4. **MediaPicker 回傳**：single mode 回傳 string，multiple mode 回傳 string[]
5. **容量計算**：媒體庫的已用容量是估算，KV 不直接提供總量，需遍歷所有 media key 的 size
6. **Analytics 日期格式**：\`YYYY-MM-DD\`，時區用 UTC
7. **登入狀態持久化**：使用 \`sessionStorage\` 而非 \`localStorage\`，避免安全性問題
`;
